/**
 * Portal basket item assignment UI.
 *
 * Handles staff typeahead search, adding/removing assignment rows,
 * saving assignments via AJAX, and the "send to Tracker" toggle.
 */

import { sanitiseEmbroideryTextWithin } from "../embroidery-text.js";

// Listens on the document so rows cloned from the template are covered too
sanitiseEmbroideryTextWithin(document, ".item-assignment__personalisation-field");

const CSRF = () => document.querySelector('meta[name=csrf-token]').getAttribute('content');

// ---------------------------------------------------------------------------
// Sidebar fee / total update helpers
// ---------------------------------------------------------------------------

function formatCurrency(value) {
    return '£' + Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function updatePriceDisplay(container, exVat, incVat) {
    const exEl  = container.querySelector('.ex_vat_price');
    const incEl = container.querySelector('.inc_vat_price');
    if (exEl) {
        exEl.dataset.raw = exVat;
        const v = exEl.querySelector('.ex_vat_value');
        if (v) v.textContent = formatCurrency(exVat);
    }
    if (incEl) {
        incEl.dataset.raw = incVat;
        const v = incEl.querySelector('.inc_vat_value');
        if (v) v.textContent = formatCurrency(incVat);
    }
}

function updateSidebarFee(data) {
    if (data.manbagFeeExVat === undefined) return;

    const feeRow    = document.getElementById('manbag-fee-row');
    const feeCell   = document.getElementById('manbag-fee-price');
    const totalCell = document.getElementById('basket-total-price');

    if (feeRow) {
        feeRow.style.display = data.manbagFeeExVat > 0 ? '' : 'none';
    }
    if (feeCell) {
        updatePriceDisplay(feeCell, data.manbagFeeExVat, data.manbagFeeIncVat);
    }
    if (totalCell) {
        updatePriceDisplay(totalCell, data.displayTotalExVat, data.displayTotalIncVat);
    }
}

// ---------------------------------------------------------------------------
// Typeahead
// ---------------------------------------------------------------------------

const TYPEAHEAD_DEBOUNCE_MS = 250;

/**
 * In-flight staff lookups, keyed by name input.
 *
 * A save that fires while a lookup is still pending would otherwise send the
 * raw typed name with no staff id, which used to create a duplicate staff
 * record for a person who already existed. `awaitPendingLookups()` lets the
 * save wait for the id instead.
 */
const pendingLookups = new WeakMap();

/**
 * Whitespace-collapsed, lower-cased form of a name.
 *
 * Mirrors PortalStaff::normaliseName() on the server so the two agree on what
 * counts as the same name. Without the whitespace collapse, "John  Smith"
 * would fail to auto-select and go up as a bare name for the server to resolve.
 */
function normaliseName(value) {
    return String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * The alternative names a staff result carries. The search endpoint always
 * sends the key, but a missing or malformed one means "none" rather than a
 * broken dropdown.
 */
function aliasesOf(staff) {
    return Array.isArray(staff.aliases) ? staff.aliases : [];
}

/**
 * The result the typed string names outright.
 *
 * Follows the same order as PortalStaff::findByNameForPortal(): exact live
 * name first, then an alias left behind by a merge. Matching an alias here is
 * what stops someone typing a merged duplicate's old name and getting no
 * client-side resolution at all.
 */
function findExactMatch(results, q) {
    const typed = normaliseName(q);

    if (!typed) return null;

    return results.find(staff => normaliseName(staff.full_name) === typed)
        ?? results.find(staff => aliasesOf(staff).some(alias => normaliseName(alias) === typed))
        ?? null;
}

/**
 * The alias that explains why a result is in the list, or null when the live
 * name already explains it.
 *
 * Someone who types "Will" and sees "William Stewart" needs to be told the row
 * is there because of the alias, otherwise the suggestion looks like a mistake.
 */
function matchedAlias(staff, q) {
    const typed = normaliseName(q);

    if (!typed) return null;
    if (normaliseName(staff.full_name).includes(typed)) return null;

    const aliases = aliasesOf(staff);

    return aliases.find(alias => normaliseName(alias) === typed)
        ?? aliases.find(alias => normaliseName(alias).includes(typed))
        ?? null;
}

/**
 * Build one suggestion row: the staff member's real name, plus a muted
 * "also known as" line when the match came from an alias.
 */
function buildSuggestion(staff, q) {
    const li = document.createElement('li');
    li.dataset.staffId = staff.id;

    const nameEl = document.createElement('span');
    nameEl.className = 'item-assignment__suggestion-name';
    nameEl.textContent = staff.full_name;
    li.appendChild(nameEl);

    const alias = matchedAlias(staff, q);

    if (alias) {
        const aliasEl = document.createElement('span');
        aliasEl.className = 'item-assignment__suggestion-alias';
        aliasEl.textContent = `also known as ${alias}`;
        li.appendChild(aliasEl);
    }

    return li;
}

/**
 * Search staff for the typed string and, on an exact match against either the
 * full name or one of the aliases, adopt that staff id. Pass render: false to
 * resolve the id only, without opening the suggestions dropdown (used on blur,
 * where showing it would be wrong).
 */
async function runStaffSearch(input, suggestions, q, { render = true } = {}) {
    const searchUrl = input.closest('.item-assignment').dataset.staffSearchUrl;

    try {
        const res = await fetch(`${searchUrl}?q=${encodeURIComponent(q)}`, {
            headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': CSRF() }
        });
        const results = await res.json();

        // Ignore a response that landed after the user carried on typing.
        if (input.value.trim() !== q) return;

        if (render) suggestions.innerHTML = '';

        if (results.length === 0) {
            if (render) suggestions.hidden = true;
            input.dataset.staffId = '';
            return;
        }

        const exactMatch = findExactMatch(results, q);
        if (exactMatch) {
            input.dataset.staffId = exactMatch.id;
            input.classList.remove('is-invalid');
            suggestions.hidden = true;
            suggestions.innerHTML = '';
            markDirty(input.closest('.item-assignment'));
            return;
        }

        if (!render) return;

        results.forEach(staff => {
            const li = buildSuggestion(staff, q);
            li.addEventListener('mousedown', e => {
                e.preventDefault();
                input.value = staff.full_name;
                input.dataset.staffId = staff.id;
                input.classList.remove('is-invalid');
                suggestions.hidden = true;
                markDirty(input.closest('.item-assignment'));
            });
            suggestions.appendChild(li);
        });

        suggestions.hidden = false;
    } catch (e) {
        if (render) suggestions.hidden = true;
    }
}

function bindTypeahead(input, suggestions) {
    // Per-input timer. A single shared timer meant typing in one row cancelled
    // the pending lookup for another, leaving that row with no staff id and
    // duplicating an existing person on save.
    let debounceTimer = null;

    input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        const q = input.value.trim();

        if (q.length < 2) {
            suggestions.hidden = true;
            suggestions.innerHTML = '';
            input.dataset.staffId = '';
            pendingLookups.delete(input);
            return;
        }

        pendingLookups.set(input, new Promise(resolve => {
            debounceTimer = setTimeout(() => {
                runStaffSearch(input, suggestions, q).finally(resolve);
            }, TYPEAHEAD_DEBOUNCE_MS);
        }));
    });

    input.addEventListener('blur', () => {
        const q = input.value.trim();

        // Resolve straight away rather than waiting out the debounce, so
        // tabbing or clicking away still picks up an existing staff id.
        if (q.length >= 2 && !input.dataset.staffId) {
            clearTimeout(debounceTimer);
            pendingLookups.set(input, runStaffSearch(input, suggestions, q, { render: false }));
        }

        setTimeout(() => { suggestions.hidden = true; }, 150);
    });
}

/**
 * Wait for any outstanding staff lookups in this container to settle.
 */
async function awaitPendingLookups(container) {
    const lookups = [];

    container.querySelectorAll('.item-assignment__name-input').forEach(input => {
        const pending = pendingLookups.get(input);
        if (pending) lookups.push(pending);
    });

    if (lookups.length) await Promise.all(lookups);
}

// ---------------------------------------------------------------------------
// Build payload from a container's rows
// ---------------------------------------------------------------------------

function buildPayload(container) {
    const rows = container.querySelectorAll('.item-assignment__row');
    const assignments = [];

    rows.forEach(row => {
        const nameInput = row.querySelector('.item-assignment__name-input');
        const qtyInput  = row.querySelector('.quantity_selector_quantity_field');
        const staffId   = nameInput.dataset.staffId ? parseInt(nameInput.dataset.staffId) : null;
        const name      = nameInput.value.trim();
        const qty       = parseInt(qtyInput.value) || 0;

        if (!name && !staffId) return;
        if (qty < 1) return;

        const entry = {
            portal_staff_id: staffId || null,
            name: staffId ? null : name,
            quantity: qty,
            personalisation_data: null,
        };

        // Gather personalisation data if present
        const personalisationUnits = row.querySelectorAll('.item-assignment__personalisation-unit');
        if (personalisationUnits.length > 0) {
            const pd = {};
            personalisationUnits.forEach(unit => {
                const unitIndex = parseInt(unit.dataset.unit);
                unit.querySelectorAll('.item-assignment__personalisation-field').forEach(field => {
                    const pid  = field.dataset.personalisationId;
                    const line = parseInt(field.dataset.line) - 1;
                    if (!pd[pid]) pd[pid] = {};
                    if (!pd[pid][unitIndex]) pd[pid][unitIndex] = [];
                    pd[pid][unitIndex][line] = field.value;
                });
            });
            entry.personalisation_data = pd;
        }

        assignments.push(entry);
    });

    return assignments;
}

// ---------------------------------------------------------------------------
// Validate — name required whenever qty > 0
// ---------------------------------------------------------------------------

function validateContainer(container) {
    let valid = true;
    container.querySelectorAll('.item-assignment__row').forEach(row => {
        const nameInput = row.querySelector('.item-assignment__name-input');
        const qtyInput  = row.querySelector('.quantity_selector_quantity_field');
        const qty  = parseInt(qtyInput?.value) || 0;
        const name = nameInput?.value.trim();

        if (qty > 0 && !name) {
            nameInput.classList.add('is-invalid');
            valid = false;
        }
    });
    return valid;
}

// ---------------------------------------------------------------------------
// Dirty state — show/hide the save button
// ---------------------------------------------------------------------------

function markDirty(container) {
    const saveBtn  = container.querySelector('.item-assignment__save');
    const savedMsg = container.querySelector('.item-assignment__saved');
    if (saveBtn)  saveBtn.hidden  = false;
    if (savedMsg) savedMsg.hidden = true;
}

function markClean(container) {
    const saveBtn  = container.querySelector('.item-assignment__save');
    const savedMsg = container.querySelector('.item-assignment__saved');
    if (saveBtn) saveBtn.hidden = true;
    if (savedMsg) {
        savedMsg.hidden = container.querySelectorAll('.item-assignment__row').length === 0;
    }
}

// ---------------------------------------------------------------------------
// Update the assigned/total counter
// ---------------------------------------------------------------------------

function updateCount(container) {
    const rows      = container.querySelectorAll('.item-assignment__row');
    const totalQty  = parseInt(container.dataset.totalQty) || 0;
    const counter   = container.querySelector('.item-assignment__assigned-count');
    if (!counter) return;

    let assigned = 0;
    rows.forEach(row => {
        const qtyInput = row.querySelector('.quantity_selector_quantity_field');
        assigned += parseInt(qtyInput?.value) || 0;
    });

    counter.textContent = assigned;
    const statusEl = counter.closest('.item-assignment__status');
    statusEl.classList.toggle('has-unassigned', assigned < totalQty);
    statusEl.classList.remove('is-fully-assigned');
    if (assigned > 0 && assigned >= totalQty) {
        void statusEl.offsetWidth; // force reflow so animation replays each time
        statusEl.classList.add('is-fully-assigned');
    }

    // Recalculate each row's max so combined total can never exceed totalQty
    rows.forEach(row => {
        const qtyInput = row.querySelector('.quantity_selector_quantity_field');
        if (!qtyInput) return;
        const thisQty = parseInt(qtyInput.value) || 0;
        const rowMax  = totalQty - (assigned - thisQty);
        qtyInput.setAttribute('max', rowMax);
        const increaseBtn = qtyInput.nextElementSibling;
        if (increaseBtn) increaseBtn.disabled = thisQty >= rowMax;
    });

    updateGlobalUnassignedWarning();
}

// ---------------------------------------------------------------------------
// Global unassigned warning (recomputed from all containers)
// ---------------------------------------------------------------------------

function updateGlobalUnassignedWarning() {
    const warning = document.querySelector('.assignment-warning');
    if (!warning) return;

    let totalQty   = 0;
    let assignedQty = 0;

    document.querySelectorAll('.item-assignment').forEach(container => {
        const rows = container.querySelectorAll('.item-assignment__row');
        totalQty += parseInt(container.dataset.totalQty) || 0;
        rows.forEach(row => {
            assignedQty += parseInt(row.querySelector('.quantity_selector_quantity_field')?.value) || 0;
        });
    });

    const unassigned = Math.max(0, totalQty - assignedQty);
    warning.hidden = unassigned === 0;

    const strong = warning.querySelector('strong');
    if (strong) {
        strong.textContent = `${unassigned} ${unassigned === 1 ? 'item' : 'items'} unassigned`;
    }
}

// ---------------------------------------------------------------------------
// Add a new row by cloning the template
// ---------------------------------------------------------------------------

function addRow(container) {
    const template = container.querySelector('.item-assignment__row-template');
    if (!template) return;

    const clone = template.content.cloneNode(true);
    const row = clone.querySelector('.item-assignment__row');
    const rowsContainer = container.querySelector('.item-assignment__rows');
    rowsContainer.appendChild(row);

    bindRow(row, container);
    updateCount(container);
    markDirty(container);
}

// ---------------------------------------------------------------------------
// Bind event listeners to a single row
// ---------------------------------------------------------------------------

function bindRow(row, container) {
    const nameInput   = row.querySelector('.item-assignment__name-input');
    const suggestions = row.querySelector('.item-assignment__suggestions');
    const qtySelector = row.querySelector('.quantity_selector');
    const qtyInput    = qtySelector?.querySelector('.quantity_selector_quantity_field');
    const removeBtn   = row.querySelector('.item-assignment__remove');

    if (nameInput && suggestions) {
        bindTypeahead(nameInput, suggestions);
        nameInput.addEventListener('input', () => {
            nameInput.classList.remove('is-invalid');
            markDirty(container);
        });
    }

    if (qtySelector && qtyInput) {
        qtySelector.addEventListener('click', e => {
            const min = parseInt(qtyInput.getAttribute('min')) || 0;
            const max = parseInt(qtyInput.getAttribute('max')) || Infinity;
            let val   = parseInt(qtyInput.value) || 0;

            if (e.target.classList.contains('increase')) {
                val = Math.min(val + 1, max);
            } else if (e.target.classList.contains('decrease')) {
                val = Math.max(val - 1, min);
            } else {
                return;
            }

            qtyInput.value = val;
            qtyInput.previousElementSibling.disabled = val <= min;
            qtyInput.nextElementSibling.disabled     = val >= max;

            updatePersonalisationUnits(row, val, container);
            updateCount(container);
            markDirty(container);
        });

        qtyInput.addEventListener('change', () => {
            const min = parseInt(qtyInput.getAttribute('min')) || 0;
            const max = parseInt(qtyInput.getAttribute('max')) || Infinity;
            const val = Math.min(Math.max(parseInt(qtyInput.value) || min, min), max);
            qtyInput.value = val;
            qtyInput.previousElementSibling.disabled = val <= min;
            qtyInput.nextElementSibling.disabled     = val >= max;

            updatePersonalisationUnits(row, val, container);
            updateCount(container);
            markDirty(container);
        });
    }

    removeBtn?.addEventListener('click', () => {
        row.remove();
        updateCount(container);
        markDirty(container);
    });
}

// ---------------------------------------------------------------------------
// When qty changes on a personalised row, re-render personalisation units
// ---------------------------------------------------------------------------

function updatePersonalisationUnits(row, newQty, container) {
    const pdContainer = row.querySelector('.item-assignment__personalisations');
    if (!pdContainer) return;

    const isPersonalised = container.dataset.personalised === 'true';
    if (!isPersonalised) return;

    const existing = pdContainer.querySelectorAll('.item-assignment__personalisation-unit');
    const currentCount = existing.length;

    if (newQty > currentCount) {
        const templateUnit = existing[0];
        if (!templateUnit) return;

        for (let i = currentCount + 1; i <= newQty; i++) {
            const clone = templateUnit.cloneNode(true);
            clone.dataset.unit = i;
            clone.querySelector('label.item-assignment__personalisation-unit-label').textContent = `Item ${i} Personalisation`;
            clone.querySelectorAll('input').forEach(input => {
                input.dataset.unit = i;
                input.value = '';
            });
            pdContainer.appendChild(clone);
        }
    } else if (newQty < currentCount) {
        for (let i = currentCount; i > newQty; i--) {
            existing[i - 1]?.remove();
        }
    }
}

// ---------------------------------------------------------------------------
// Save a single container's assignments via AJAX
// ---------------------------------------------------------------------------

async function saveAssignments(container, saveBtn) {
    if (!validateContainer(container)) return;

    if (saveBtn) {
        saveBtn.disabled = true;
        saveBtn.textContent = 'Saving…';
    }

    // Must happen before the payload is built, so rows whose lookup was still
    // in flight go up with their staff id rather than a bare name.
    await awaitPendingLookups(container);

    const url         = container.dataset.saveUrl;
    const assignments = buildPayload(container);

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': CSRF(),
            },
            body: JSON.stringify({ assignments }),
        });

        if (res.status === 419) {
            window.location.reload();
            return;
        }

        const data = await res.json();

        if (saveBtn) {
            saveBtn.textContent = data.success ? 'Saved' : 'Error';
            saveBtn.disabled = false;
            if (data.success) {
                updateSidebarFee(data);
                setTimeout(() => {
                    saveBtn.textContent = 'Save Assignments';
                    markClean(container);
                }, 2000);
            }
        }
    } catch {
        if (saveBtn) {
            saveBtn.textContent = 'Error';
            saveBtn.disabled = false;
        }
    }
}

// ---------------------------------------------------------------------------
// Bind a whole assignment container (one per basket item)
// ---------------------------------------------------------------------------

function bindContainer(container) {
    const addBtn  = container.querySelector('.item-assignment__add');
    const saveBtn = container.querySelector('.item-assignment__save');

    addBtn?.addEventListener('click', () => addRow(container));
    saveBtn?.addEventListener('click', () => saveAssignments(container, saveBtn));

    container.querySelectorAll('.item-assignment__row').forEach(row => bindRow(row, container));

    updateCount(container);
}

// ---------------------------------------------------------------------------
// "Send to Tracker" checkbox
// ---------------------------------------------------------------------------

function bindTrackerToggle() {
    const toggle = document.getElementById('send_assignments_to_tracker');
    if (!toggle) return;

    toggle.addEventListener('change', async () => {
        try {
            const res  = await fetch(toggle.dataset.toggleUrl, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': CSRF(),
                },
                body: JSON.stringify({ send_to_tracker: toggle.checked }),
            });
            const data = await res.json();
            updateSidebarFee(data);
        } catch {
            // revert optimistic UI on failure
            toggle.checked = !toggle.checked;
        }
    });
}

// ---------------------------------------------------------------------------
// Initialise
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.item-assignment').forEach(container => bindContainer(container));
    bindTrackerToggle();
});
